import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import HighlightText from '../components/HighlightText';
import TutorProfileModal from '../components/TutorProfileModal';
import { allTutors } from '../data/tutorsData';

const { FiFilter, FiStar, FiCheckCircle, FiUser, FiMapPin, FiBook, FiPhone, FiX, FiAward } = FiIcons;

const tutorsPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Find Home Tutors in Lucknow | MyHomeTutors',
  description: 'Browse 500+ verified home tutors in Lucknow for all classes, subjects, IIT-JEE and NEET preparation. Filter by subject, location and class.',
  url: 'https://myhometutors.org/tutors',
  publisher: {
    '@type': 'Organization',
    name: 'MyHomeTutors',
    telephone: '+919415905584',
  },
  mainEntity: {
    '@type': 'ItemList',
    name: 'Home Tutors in Lucknow',
    numberOfItems: allTutors.length,
    itemListElement: allTutors.slice(0, 10).map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Person',
        name: t.name,
        description: t.qualifications,
        jobTitle: 'Home Tutor',
        knowsAbout: t.subjects,
      },
    })),
  },
};

const BADGE_STYLES = {
  'IIT Expert': 'bg-blue-600 text-white',
  'NEET Expert': 'bg-green-600 text-white',
};

const TutorCard = ({ tutor, onViewProfile }) => (
  <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group">
    <div className="flex gap-4 mb-4">
      <div className="relative flex-shrink-0">
        <img src={tutor.img} alt={tutor.name} className="w-[72px] h-[72px] rounded-2xl object-cover border-2 border-red-100 shadow-sm" />
        {tutor.verified && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
            <FiCheckCircle className="text-white w-3 h-3 fill-current" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <h4 className="font-extrabold text-slate-900 text-base tracking-tight truncate">{tutor.name}</h4>
          {tutor.badge && (
            <span className={`flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${BADGE_STYLES[tutor.badge] || 'bg-primary text-white'}`}>
              <FiAward className="w-2.5 h-2.5" /> {tutor.badge}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 font-semibold mt-0.5 truncate">{tutor.qualifications}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs bg-blue-50 text-blue-600 font-bold px-2 py-0.5 rounded-full">{tutor.exp}</span>
          <span className="text-xs bg-gray-100 text-gray-600 font-bold px-2 py-0.5 rounded-full">{tutor.gender}</span>
        </div>
        <div className="flex items-center gap-0.5 mt-1.5">
          {[...Array(5)].map((_, i) => <FiStar key={i} className="w-3 h-3 fill-current text-yellow-400" />)}
        </div>
      </div>
    </div>
    <div className="space-y-1.5 mb-4 text-sm">
      <div className="flex items-center gap-2">
        <FiBook className="text-primary w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-semibold text-slate-700 truncate">{tutor.subjects.join(', ')}</span>
      </div>
      <div className="flex items-center gap-2">
        <FiUser className="text-primary w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-semibold text-slate-600 truncate">{tutor.classes.join(', ')}</span>
      </div>
      <div className="flex items-center gap-2">
        <FiMapPin className="text-primary w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-semibold text-slate-600 truncate">{tutor.locations.join(', ')}</span>
      </div>
    </div>
    <div className="flex items-center gap-2 mt-auto">
      <button onClick={() => onViewProfile(tutor)} className="flex-1 bg-gray-50 text-slate-700 border border-gray-200 py-2.5 rounded-xl text-sm font-extrabold hover:bg-gray-100 transition text-center" >
        View Profile
      </button>
      <a href="tel:+919415905584" className="flex-1 bg-primary text-white py-2.5 rounded-xl text-sm font-extrabold hover:bg-primary-hover transition shadow-sm text-center flex items-center justify-center gap-1.5" >
        <FiPhone className="w-3.5 h-3.5" /> Book Demo
      </a>
    </div>
  </div>
);

const FilterGroup = ({ title, options, selected, setter, badgeMap }) => {
  const toggle = (val) => setter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  return (
    <div className="mb-6 pb-6 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
      <h4 className="font-extrabold text-slate-800 text-xs mb-3 uppercase tracking-wider">{title}</h4>
      <div className="space-y-2 max-h-44 overflow-y-auto pr-1 custom-scrollbar">
        {options.map((opt, idx) => {
          const isSelected = selected.includes(opt);
          const badge = badgeMap?.[opt];
          return (
            <button key={idx} onClick={() => toggle(opt)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all text-sm font-medium ${isSelected ? 'bg-red-50 text-primary border border-red-200' : 'hover:bg-gray-50 text-slate-600 border border-transparent'}`} >
              <div className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-primary border-primary' : 'border-gray-300'}`} >
                {isSelected && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="truncate flex-1">{opt}</span>
              {badge && (
                <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full flex-shrink-0 ${BADGE_STYLES[badge] || 'bg-gray-200 text-gray-600'}`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TutorsPage = () => {
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);

  const allSubjects = useMemo(() => [...new Set(allTutors.flatMap((t) => t.subjects))].sort(), []);
  const allLocations = useMemo(() => [...new Set(allTutors.flatMap((t) => t.locations))].sort(), []);
  const allGenders = ['Male', 'Female'];
  
  const allClasses = useMemo(() => {
    const base = [...new Set(allTutors.flatMap((t) => t.classes))];
    const special = ['IIT-JEE', 'NEET'];
    const regular = base.filter((c) => !special.includes(c)).sort();
    const present = special.filter((c) => base.includes(c));
    return [...regular, ...present];
  }, []);

  const classBadgeMap = useMemo(() => ({
    'IIT-JEE': 'IIT Expert',
    'NEET': 'NEET Expert'
  }), []);

  const toggle = (setter, value) => {
    setter((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  };

  const clearAll = () => {
    setSelectedSubjects([]);
    setSelectedLocations([]);
    setSelectedClasses([]);
    setSelectedGenders([]);
  };

  const hasFilters = selectedSubjects.length > 0 || selectedLocations.length > 0 || selectedClasses.length > 0 || selectedGenders.length > 0;

  const filteredTutors = useMemo(() => {
    return allTutors.filter((t) => {
      const matchSubj = selectedSubjects.length === 0 || t.subjects.some((s) => selectedSubjects.includes(s));
      const matchLoc = selectedLocations.length === 0 || t.locations.some((l) => selectedLocations.includes(l));
      const matchGender = selectedGenders.length === 0 || selectedGenders.includes(t.gender);
      const matchClass = selectedClasses.length === 0 || t.classes.some((c) => selectedClasses.includes(c));
      return matchSubj && matchLoc && matchGender && matchClass;
    });
  }, [selectedSubjects, selectedLocations, selectedGenders, selectedClasses]);

  const allActive = [
    ...selectedSubjects.map((v) => ({ v, clear: () => toggle(setSelectedSubjects, v) })),
    ...selectedLocations.map((v) => ({ v, clear: () => toggle(setSelectedLocations, v) })),
    ...selectedGenders.map((v) => ({ v, clear: () => toggle(setSelectedGenders, v) })),
    ...selectedClasses.map((v) => ({ v, clear: () => toggle(setSelectedClasses, v) })),
  ];

  return (
    <div className="bg-[#fdf8f4] min-h-screen py-10">
      <Helmet>
        <title>Find Home Tutors in Lucknow | Browse 500+ Verified Tutors - MyHomeTutors</title>
        <meta name="description" content="Browse 500+ verified home tutors in Lucknow for all classes, subjects, IIT-JEE and NEET preparation. Filter by subject, location and class." />
        <link rel="canonical" href="https://myhometutors.org/tutors/" />
        <meta property="og:title" content="Find Home Tutors in Lucknow | Browse 500+ Verified Tutors" />
        <meta property="og:description" content="Browse 500+ verified home tutors in Lucknow for all classes, subjects, IIT-JEE and NEET preparation." />
        <meta property="og:url" content="https://myhometutors.org/tutors" />
        <meta property="og:type" content="website" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(tutorsPageSchema) }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Find Your <HighlightText>Perfect Tutor</HighlightText>
          </h1>
          <p className="text-gray-500 font-medium max-w-2xl mx-auto">
            Browse our verified expert tutors. Use filters to find exactly who you need.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <button
            className="lg:hidden w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between font-bold text-slate-700 shadow-sm"
            onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          >
            <span className="flex items-center gap-2">
              <FiFilter className="text-primary" /> Filters
              {hasFilters && <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">{allActive.length}</span>}
            </span>
            <span className="bg-red-50 text-primary px-3 py-1 rounded-full text-xs font-bold">{filteredTutors.length} Tutors</span>
          </button>

          <div
            className={`w-full lg:w-72 flex-shrink-0 bg-white rounded-3xl p-6 shadow-sm border border-gray-100 ${isMobileFilterOpen ? 'block' : 'hidden lg:block'}`}
          >
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
              <h3 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
                <FiFilter className="text-primary" /> Filters
              </h3>
              {hasFilters && (
                <button onClick={clearAll} className="text-xs font-bold text-primary hover:text-primary-hover underline">
                  Clear All
                </button>
              )}
            </div>

            <FilterGroup title="By Subject" options={allSubjects} selected={selectedSubjects} setter={setSelectedSubjects} />
            <FilterGroup title="By Class" options={allClasses} selected={selectedClasses} setter={setSelectedClasses} badgeMap={classBadgeMap} />
            <FilterGroup title="By Location" options={allLocations} selected={selectedLocations} setter={setSelectedLocations} />
            <FilterGroup title="By Gender" options={allGenders} selected={selectedGenders} setter={setSelectedGenders} />
          </div>

          <div className="flex-1 w-full min-w-0">
            <div className="flex justify-between items-center mb-4">
              <span className="text-slate-600 font-bold text-sm">
                Showing <span className="text-primary">{filteredTutors.length}</span> of {allTutors.length} Tutors
              </span>
            </div>

            {allActive.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {allActive.map(({ v, clear }, i) => (
                  <span key={i} className="flex items-center gap-1.5 bg-red-50 text-primary text-xs font-bold px-3 py-1.5 rounded-full border border-red-100">
                    {v}
                    <button onClick={clear}><FiX className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}

            {filteredTutors.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiUser className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-2">No tutors found</h3>
                <p className="text-gray-500 font-medium">Try adjusting your filters to see more results.</p>
                <button
                  onClick={clearAll}
                  className="mt-6 bg-primary text-white px-6 py-2.5 rounded-xl font-bold shadow-md hover:bg-primary-hover transition"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {filteredTutors.map((tutor, idx) => (
                  <TutorCard key={idx} tutor={tutor} onViewProfile={setSelectedTutor} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <TutorProfileModal tutor={selectedTutor} onClose={() => setSelectedTutor(null)} />
      <style dangerouslySetInnerHTML={{
        __html: `
        .custom-scrollbar::-webkit-scrollbar{width:3px}
        .custom-scrollbar::-webkit-scrollbar-track{background:#f1f5f9;border-radius:4px}
        .custom-scrollbar::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px}
      `}} />
    </div>
  );
};

export default TutorsPage;